import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectFilteredPeopleMembers } from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import Box from 'src/components/Box/Box'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  userEmailOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import Trns from 'src/localization/Trns'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { selectUserEmailMap } from 'src/redux/selectors/data/users.selectors'
import { FormProps } from 'src/types'
import { css } from 'styled-components'

interface StaticRecipientApproverBoxProps {
  className?: string
  displayNameProps?: FormProps<string>
  displayNameFieldLabel?: React.ReactElement
  recipientFirstNameProps?: FormProps<string>
  recipientLastNameProps?: FormProps<string>
  selectedUserHasName?: boolean
  recentUserOptions?: UserOption[]
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  tooltip?: React.ReactElement
  userEmailProps: FormProps<string[]>
  isMulti?: boolean
  id?: string
  filterUserbyAssocationTypes?: UserAssociation[]
  getRecipientWarningMessage?: (
    hasSelectedUsersNotInOrg: boolean,
  ) => React.ReactElement
  setSelectedUsersNotInOrg?: (users: string[]) => void
  unableToAddNewUser?: boolean
  showRoles?: boolean
  setRoleIndex?: (index: number | undefined) => void
  errorBorder?: boolean
  cardHolderEmail?: string
  recipientEmail?: string
}

const StaticRecipientApproverBox: React.FC<StaticRecipientApproverBoxProps> = (
  props,
): React.ReactElement => {
  const userEmailMap = useSelector(selectUserEmailMap)
  const memberListModels = useSelector(selectFilteredPeopleMembers)

  const memberEmailMap = useMemo(() => {
    return Object.values(memberListModels).reduce(
      (acc: Record<string, UserListModel>, curr) => ({
        ...acc,
        [curr.email]: curr,
      }),
      {},
    )
  }, [memberListModels])

  const emailMap = useMemo(() => {
    return { ...userEmailMap, ...memberEmailMap }
  }, [userEmailMap, memberEmailMap])

  const addDeleteVal = (newSet: Set<string>, val: string): void => {
    if (newSet.has(val)) {
      newSet.delete(val)
    } else {
      newSet.add(val)
    }
  }

  const cardholderOption: UserOption[] | undefined = useMemo(() => {
    return props.cardHolderEmail !== undefined &&
      emailMap[props.cardHolderEmail] !== undefined
      ? [userEmailOption(emailMap[props.cardHolderEmail])]
      : undefined
  }, [props.cardHolderEmail])

  const recipientOption: UserOption[] | undefined = useMemo(() => {
    return props.recipientEmail !== undefined &&
      emailMap[props.recipientEmail] !== undefined
      ? [userEmailOption(emailMap[props.recipientEmail])]
      : undefined
  }, [props.recipientEmail])

  const cardHolderEmailSet =
    props.cardHolderEmail !== undefined
      ? new Set(props.cardHolderEmail)
      : undefined

  const recipientEmailSet =
    props.recipientEmail !== undefined
      ? new Set(props.recipientEmail)
      : undefined

  const handleEmailSelection = (val: string): void => {
    const newSet = new Set(props.userEmailProps.value)
    addDeleteVal(newSet, val)
    props.isMulti
      ? props.userEmailProps.onChange(Array.from(newSet))
      : props.userEmailProps.onChange([val])
  }

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          position: relative;
        `}
      >
        {cardHolderEmailSet !== undefined && (
          <UserDropdown
            css={css`
              &:nth-child(n + 2) {
                margin-top: 24px;
              }
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              z-index: 3;
            `}
            fieldLabel={<Trns source={'Submitted by'} />}
            selectedOptions={
              cardholderOption !== undefined && cardholderOption.length > 0
                ? cardholderOption
                : undefined
            }
            optionGroups={[]}
            recentOptionGroups={[]}
            isCreatable={false}
            isStatic
            errors={props.userEmailProps.errors}
            chooseOption={handleEmailSelection}
            selectedValues={cardHolderEmailSet}
          />
        )}
        {recipientEmailSet !== undefined && (
          <UserDropdown
            css={css`
              &:nth-child(n + 2) {
                margin-top: 24px;
              }
              ${getFontCss(
                16,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              z-index: 3;
            `}
            fieldLabel={<Trns source={'Recipient'} />}
            optionGroups={[]}
            recentOptionGroups={[]}
            isCreatable={false}
            isStatic
            errors={props.userEmailProps.errors}
            selectedOptions={
              recipientOption !== undefined && recipientOption.length > 0
                ? recipientOption
                : undefined
            }
            chooseOption={handleEmailSelection}
            selectedValues={recipientEmailSet}
          />
        )}
        {props.displayNameFieldLabel !== undefined && (
          <>
            <span
              css={css`
                display: flex;
                align-items: center;
                word-wrap: break-word;

                &:nth-child(n + 2) {
                  margin-top: 24px;
                }
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {props.displayNameFieldLabel}
            </span>
            <span
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )}
              `}
            >
              {props.displayNameProps?.value}
            </span>
          </>
        )}
      </div>
    </Box>
  )
}

export default StaticRecipientApproverBox
